import {Component, computed, effect, Input, OnChanges, signal, SimpleChanges, inject, ChangeDetectionStrategy} from '@angular/core';
import {BaseChartComponent, type EnergyType, type Interval} from './base-chart.component';
import {BaseChartDirective} from 'ng2-charts';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {Chart, ChartOptions} from 'chart.js';
import {de} from 'date-fns/locale';
import {getDaysInMonth} from 'date-fns';
import annotationPlugin from 'chartjs-plugin-annotation';

import {DateTimeValue} from '../../../../api/models/date-time-value';
import {environment} from '../../../../environments/environment';
import {rxResource} from '@angular/core/rxjs-interop';
import {SunResponse} from '../../../../api/models/sun-response';
import {LocationStateService} from '../../../../common/services/location-state.service';
import {getAverageEnergyStatus, getPlannedEnergyStatus, MeasuredEnergyKey} from './average-energy-response.helper';

@Component({
  selector: 'app-performance-chart',
  templateUrl: './chart.component.html',
  standalone: true,
  imports: [BaseChartDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class PerformanceChartComponent extends BaseChartComponent implements OnChanges {
  @Input() override interval: Interval = 'day'; // Define input with default value
  @Input() type: EnergyType = 'production'; // Define input with default value
  protected override get summaryType(): EnergyType { return this.type; }
  @Input() set date(value: Date) {
    this._date.set(value);
  }

  get date(): Date {
    return this._date();
  }

  private _date = signal<Date>(new Date());

  @Input() data: AverageEnergyResponse | undefined;

  // Override unit to kwh for month and year charts, kW for day charts
  @Input() override get unit(): string {
    switch (this.interval) {
      case 'month':
      case 'year':
        return 'kWh';
      default:
        return super.unit;
    }
  }

  override set unit(value: string) {
    // Allow external setting but ignore it since we determine unit based on batteryType
  }

  // Weather resource for sunrise/sunset data - automatically reloads when date changes
  // Using custom loader that directly accesses the current date from the component
  weatherResource = rxResource<SunResponse, any>({
    params: computed(() => {
      const date = this._date().toISOString();
      return {body: {date: date}};
    }),
    defaultValue: environment.showDefault
      ? {
          'sunrise': this._date().toISOString(),
          'sunset': this._date().toISOString(),
        }
      : {'sunrise': undefined, 'sunset': undefined},
    stream: this.sunDataLoader,
  });

  private readonly locationStateService = inject(LocationStateService);

  // React to location changes and update chart scales accordingly
  private readonly locationEffect = effect(() => {
    this.applyLocationRange();

    // Refresh options used by the chart and trigger redraw if needed
    const chartOptions = this.getCustomChartOptions();
    this.chartOptions.set(chartOptions);

    if (this.chart) {
      this.chart.update();
    }
  });

  // Effect that automatically adds/removes sunrise/sunset annotations based on weather data and interval
  private sunriseSunsetEffect = effect(() => {
    const sun = this.weatherResource.value();
    const currentInterval = this.interval;

    // If not day interval, clear annotations and return
    if (currentInterval !== 'day') {
      this.removeAnnotations();
      return;
    }

    // reset annotations on options and chart
    this.removeAnnotations();

    // Create a fresh copy of the line chart options to avoid mutation
    let updatedOptions = {...this.lineChartOptions};

    if (sun?.sunrise) {
      updatedOptions = this.addVerticalLine(updatedOptions, new Date(sun.sunrise), 'Sonnenaufgang', 'rgb(128,128,128)');
    }
    if (sun?.sunset) {
      updatedOptions = this.addVerticalLine(updatedOptions, new Date(sun.sunset), 'Sonnenuntergang', 'rgb(128,128,128)');
    }

    // Update the lineChartOptions with the new annotations
    this.lineChartOptions = updatedOptions;

    // refresh options signal so chart receives new reference
    this.chartOptions.set(this.getCustomChartOptions());

    // trigger chart to re-render with new options
    if (this.chart) {
      this.chart.update();
    }
  });

  lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the bottom
        display: true, // Enable legend display
      },
      annotation: {
        annotations: {},
      },
    },
    scales: {
      y: {
        stacked: true,
        ticks: {
          stepSize: 1, // Display every integer
          padding: 12, // Consistent padding
          font: {
            size: 12,
          },
        },
        min: 0, // Always start from 0 to ensure consistent scaling
        max: 10, // default, will be overridden by locationEffect
        border: {
          display: false,
        },
        grid: {
          display: true,
        },
      },
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'HH:mm',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          minRotation: 0, // Start with horizontal labels
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 24, // Default tick limit
          padding: 8, // Default padding
          font: {
            size: 12,
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  monthBarChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the bottom
        display: true, // Enable legend display
      },
    },
    scales: {
      y: {
        stacked: true,
        min: 0, // Always start from 0 to ensure consistent scaling
        ticks: {
          padding: 12, // Consistent padding
          font: {
            size: 12,
          },
        },
        border: {
          display: false,
        },
        grid: {
          display: true,
        },
      },
      x: {
        stacked: true,
        type: 'time', // Ensure time-based X-axis
        time: {
          unit: 'day', // Display days on the axis
          tooltipFormat: 'dd MM', // Tooltip format (e.g., "01 Mar")
          displayFormats: {day: 'dd MM'}, // Format for the axis labels
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          maxRotation: 90, // labels vertical for both mobile and desktop
          minRotation: 90, // labels vertical for both mobile and desktop
          callback: (value) => {
            const date = new Date(value);
            return date.toLocaleDateString('de-AT', {
              day: '2-digit',
              month: 'numeric',
              timeZone: this.timezone,
            });
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  yearBarChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the bottom
        display: true, // Enable legend display
      },
    },
    scales: {
      y: {
        stacked: true,
        min: 0, // Always start from 0 to ensure consistent scaling
        ticks: {
          padding: 12, // Consistent padding
          font: {
            size: 12,
          },
        },
        border: {
          display: false,
        },
        grid: {
          display: true,
        },
      },
      x: {
        stacked: true,
        type: 'time', // Use time scale for date-based X-axis
        time: {
          unit: 'month', // Group data by month
          tooltipFormat: 'MMM yyyy', // Show "Mar 2025" format in tooltips
          displayFormats: {
            month: 'MMM', // Display short month names (e.g., "Jan", "Feb")
            day: 'D', // Show day number (e.g., "1", "15", "30")
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          callback: (value) => {
            const date = new Date(value as string);
            return date.toLocaleDateString(environment.locale, {month: 'short', timeZone: this.timezone});
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  constructor() {
    super();
    // Register the annotation plugin
    Chart.register(annotationPlugin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Performance chart (prod or cons) received change', changes);
    if (changes['type']) {
      this.applyLocationRange();
    }
    if (changes['data'] || changes['type'] || changes['date'] || changes['interval']) {
      this.onResourceChange();
    }
  }

  // Production and consumption use separate y-axis ranges of the location
  private applyLocationRange() {
    const displayChart = this.locationStateService.currentLocation()?.displayChart;
    const range = this.type === 'consumption' ? displayChart?.consumption : displayChart?.performance;

    if (range && this.lineChartOptions.scales?.['y']) {
      this.lineChartOptions.scales['y'].min = range.minkWh ?? undefined;
      this.lineChartOptions.scales['y'].max = range.maxkWh ?? undefined;
      this.lineChartOptions = {...this.lineChartOptions};
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return; // Exit if no data is available
    }
    this.initializeChartData();
  }

  private initializeChartData() {
    if (!this.data) {
      return; // Exit if no data is available
    }

    const statusKey: MeasuredEnergyKey | undefined =
      this.type === 'production' || this.type === 'consumption' ? this.type : undefined;
    const measuredStatus = statusKey ? getAverageEnergyStatus(this.data, statusKey) : undefined;
    const dataItems: any[] = measuredStatus ? [measuredStatus] : [];
    const datasets: any[] = [];
    const allLabels: Date[][] = [];

    let index = 0;
    dataItems.forEach((dataItem) => {
      if (!dataItem || !dataItem.deviceTimeValues) return;

      // Add original deviceTimeValues datasets
      Object.entries(dataItem.deviceTimeValues).forEach(([key, timeValues]) => {
        // Type guard to ensure timeValues is an array of DateTimeValue
        if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
        // Ensure timeValues is complete
        const fillMode = this.interval === 'month' || this.interval === 'year' ? 'null' : 'zero';
        const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date, fillMode, dataItem.timeBucket);
        const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({...tv}));

        // if interval is monthly, we need to multiply the data to get kwH values
        if (this.interval === 'month') {
          rawTimeValues.forEach((item) => {
            if (item.read) {
              const date = new Date(item.read);
              const today = new Date();
              const multiplier = date.toDateString() === today.toDateString() ? today.getHours() : 24;
              if (item.value != null) {
                item.value = item.value * multiplier; // Multiply by current hour if today, otherwise 24
              }
            }
          });
        }
        // if interval is yearly, we need to multiply the data by the number of days per month to get kwH values
        if (this.interval === 'year') {
          rawTimeValues.forEach((item) => {
            if (item.read) {
              const date = new Date(item.read);
              const today = new Date();
              const isCurrentMonth = date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
              const multiplier = isCurrentMonth ? today.getDate() : getDaysInMonth(date.getMonth() + 1);
              if (item.value != null) {
                item.value = item.value * 24 * multiplier; // Multiply by current day if current month, otherwise days in month
              }
            }
          });
        }


        // Extract labels from this dataset
        const datasetLabels: Date[] = [];
        completedTimeValues.forEach((item) => {
          const date = new Date(item.read ?? '');
          if (!isNaN(date.getTime())) {
            datasetLabels.push(date);
          }
        });
        allLabels.push(datasetLabels);

        // set color and label for dataset
        const color = this.getCustomColor(this.type, index++); // Random yellow color
        const legendLabel = dataItem.deviceNames?.[key] || key; // Use device name or key as label
        datasets.push({
          label: legendLabel, // Use device name or key as label
          data: rawTimeValues.map((v) => v.value), // Extract values for the dataset
          borderColor: color,
          backgroundColor: color,
          fill: true,
          pointRadius: 0, // Hide dots for better readability
          pointHoverRadius: 0,
          tension: 0.1, // Smooth interpolation between points
          cubicInterpolationMode: 'monotone',
          spanGaps: false,
          borderWidth: 1,
        });
      });
    });

    // Planned values are a day-chart concept only; month/year stay measured-only.
    if (this.interval === 'day' && statusKey) {
      this.appendPlannedDatasets(
        {datasets, allLabels},
        getPlannedEnergyStatus(this.data, statusKey),
        {
          date: this.date,
          interval: this.interval,
          fillMode: 'zero',
          timeBucket: measuredStatus?.timeBucket,
          color: (deviceKey, plannedIndex) => this.getCustomColor(this.type, plannedIndex),
          label: (deviceLabel) => this.getPlannedLabel(deviceLabel),
          dataset: () => ({
            fill: true,
            tension: 0.1,
            cubicInterpolationMode: 'monotone',
            // Own stack so planned is drawn next to, not on top of, the measured series.
            stack: 'planned',
          }),
        },
      );
    }

    // Process all labels to ensure they match
    const labels = this.processLabels(allLabels);
    this.updateChart(labels, datasets);
  }

  private updateChart(labels: any[], datasets: any[]) {
    super.updateChartType(this.interval);
    const chartOptions = this.getCustomChartOptions();
    this.chartOptions.set(chartOptions);
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  private removeAnnotations() {
    // Clear annotations from chart options
    if (this.lineChartOptions.plugins?.annotation) {
      this.lineChartOptions.plugins.annotation.annotations = {};
    }

    // Clear annotations from chart instance
    if (this.chart && this.chart.options && this.chart.options.plugins && this.chart.options.plugins.annotation) {
      this.chart.options.plugins.annotation.annotations = {};
    }

    if (this.chart) {
      this.chart.update();
    }
  }

  private getCustomChartOptions() {
    let options: ChartOptions;

    switch (this.interval) {
      case 'day':
        // Re-apply the current-time marker on every rebuild (e.g. after switching month -> day)
        options = this.addCurrentTimeLine({...this.lineChartOptions}, this._date());
        break;
      case 'month':
        options = {...this.monthBarChartOptions};
        break;
      case 'year':
        options = {...this.yearBarChartOptions};
        break;
      default:
        options = this.addCurrentTimeLine({...this.lineChartOptions}, this._date());
    }

    // Apply mobile-specific settings
    this.applyMobileSettings(options);
    return options;
  }

  private applyMobileSettings(options: ChartOptions) {
    if (this.isMobile()) {
      // Apply mobile-specific Y-axis settings
      if (options.scales?.['y']) {
        const yScale = options.scales['y'];
        yScale.ticks = {
          ...yScale.ticks,
          padding: 8, // Less padding on mobile
          font: {
            size: 10, // Smaller font on mobile
          },
        };
      }

      // Apply mobile-specific X-axis settings
      if (options.scales?.['x']) {
        const xScale = options.scales['x'];
        xScale.ticks = {
          ...xScale.ticks,
          minRotation: 90, // Vertical labels on mobile
          maxRotation: 90,
          maxTicksLimit: 12, // Fewer labels on mobile
          padding: 4, // Less padding on mobile
          font: {
            size: 10, // Smaller font on mobile
          },
        };
      }
    }
  }

  private getCustomColor(option: string, index: number): string {
    if (option === 'production') {
      return this.getYellow(index);
    }
    if (option === 'consumption') {
      return this.getBlue(index);
    }
    return '#000000'; // Default color if no match
  }

  protected getYellow(index: number): string {
    const root = document.documentElement;
    const yellowPalette = [
      getComputedStyle(root).getPropertyValue('--color-production-yellow').trim(), // production-yellow
      '#FDD835', // golden yellow
      '#FFEB3B', // classic yellow
      '#F9A825', // amber yellow
      '#FBC02D', // mustard
      '#F57F17', // deep yellow-orange
    ];
    return yellowPalette[index % yellowPalette.length];
  }

  protected getBlue(index: number): string {
    const root = document.documentElement;
    const bluePalette = [
      getComputedStyle(root).getPropertyValue('--color-consumption-blue').trim(), // primary blue from CSS variable
      '#1976D2', // standard blue
      '#2196F3', // light blue
      '#1565C0', // deep blue
      '#42A5F5', // sky blue
      '#0D47A1', // dark blue
    ];
    return bluePalette[index % bluePalette.length];
  }
}
